<template>
    <div class="sso-entrada">
        <v-progress-circular v-if="!erro" indeterminate size="42" width="3" color="primary" />

        <p v-if="!erro" class="sso-entrada__texto">{{ $t('common.messages.info.openingSession') }}</p>

        <template v-else>
            <span class="material-symbols-outlined sso-entrada__icone">lock</span>
            <p class="sso-entrada__texto">{{ $t('common.messages.errors.ssoFailed') }}</p>
            <v-btn color="primary" variant="flat" @click="voltar">
                {{ $t('common.labels.backToAdmin') }}
            </v-btn>
        </template>
    </div>
</template>

<script setup>
/**
 * Unica porta de entrada do accounts.
 *
 * Recebe o handoff que o admin mandou no fragmento -- ticket (atual) ou token
 * (formato anterior, ver services/sso.js) --, guarda a sessao na origem daqui e
 * reconstroi o estado a partir da API. De proposito nao recebe usuario nem
 * permissoes pelo link: copia de estado nasce velha, e o /me e o catalogo de
 * permissoes ja sao a fonte de verdade.
 */
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { setAuth, setUser, trocarTicket } from '@/services/api/auth.js';
import { useAuthStore } from '@/stores/authStore.js';
import { lerHandoff, limparFragmento, limparTentativasDeRetorno, voltarParaOAdmin, DESTINO_PADRAO } from '@/services/sso.js';

const router = useRouter();
const authStore = useAuthStore();
const erro = ref(false);

function voltar() {
    voltarParaOAdmin({ motivo: 'sessao-invalida' });
}

onMounted(async () => {
    const handoff = lerHandoff();

    if (!handoff) {
        // Chegou em /sso sem token: ou o link veio quebrado, ou alguem digitou
        // a URL na mao. Quem sabe resolver isso e o admin.
        if (!voltarParaOAdmin({ motivo: 'sem-token' })) erro.value = true;
        return;
    }

    try {
        let destino = handoff.destino;

        if (handoff.tipo === 'ticket') {
            // O ticket sozinho nao abre nada: quem vale e o token que volta da
            // troca, ja emitido para esta aplicacao.
            const sessao = await trocarTicket(handoff.ticket);

            setAuth({ token: sessao.access_token });

            if (sessao.credentials?.corp) {
                localStorage.setItem('corp', sessao.credentials.corp);
            }

            destino = sessao.redirect_to || destino;
        } else {
            setAuth({ token: handoff.token });

            if (handoff.corp) {
                localStorage.setItem('corp', handoff.corp);
            }
        }

        limparFragmento();

        await setUser();

        // A sessao vale pelo /me. Falhar ao carregar permissoes nao pode
        // derrubar a entrada: sem arvore o usuario ve menos telas, mas com a
        // sessao descartada ele nao ve nenhuma -- e nem consegue entrar para
        // resolver o que faltou.
        try {
            await authStore.fetchPermissions();
        } catch (e) {
            console.error('Nao foi possivel carregar as permissoes:', e);
        }

        authStore.scheduleTokenRefresh();

        // Entrou: o contador que protege contra pingue-pongue com o admin zera,
        // senao a terceira sessao legitima desta aba viria sem pedido de volta.
        limparTentativasDeRetorno();

        await router.replace(destino || DESTINO_PADRAO);
    } catch (e) {
        console.error('Handoff de sessao recusado:', e);
        localStorage.clear();
        limparFragmento();

        if (!voltarParaOAdmin({ motivo: 'sessao-invalida' })) erro.value = true;
    }
});
</script>

<style scoped lang="scss">
.sso-entrada {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;

    &__texto {
        color: var(--secondaryColor);
        margin: 0;
    }

    &__icone {
        font-size: 40px;
        color: var(--secondaryColor);
    }
}
</style>
